/**
 * Per-case drilldown: the visual judging surface. The input (image/email) is shown once on the
 * left (it is shared by every run for this case) next to the ground truth; each run's verdict,
 * judge score, metrics and output are lined up on the right with a field-level diff.
 *
 * Heavy output JSON is already on the compact results, but the input file is fetched lazily here
 * and the object URL is revoked when the case changes / the drawer closes (bounded memory).
 * Keyboard: Alt+Up/Down steps cases, suppressed while typing/selecting/scrolling text.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Drawer,
  Button,
  Tag,
  Collapse,
  Empty,
  Spin,
  Image,
  Tooltip,
} from "antd";
import {
  LeftOutlined,
  RightOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  DownloadOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import {
  fetchBenchmarkResultFile,
  type BenchmarkResult,
} from "@/lib/api/resources/benchmark";
import { isErrored, type CompareRow, type CompareRun } from "../lib/align";
import { formatOutput } from "../lib/output";
import { OutputDiff } from "./OutputDiff";

const IMAGE_EXT = ["jpg", "jpeg", "png", "webp", "bmp", "gif"];

const judgeColor = (score: number): string =>
  score >= 8 ? "#10b981" : score >= 5 ? "#f59e0b" : "#ef4444";

const Verdict = ({ r }: { r: BenchmarkResult | null }) => {
  if (r == null) return <Tag>not run</Tag>;
  if (isErrored(r)) return <Tag color="error">Error</Tag>;
  if (r.correct === true) return <Tag color="success">Match</Tag>;
  if (r.correct === false) return <Tag color="warning">Mismatch</Tag>;
  return <Tag>—</Tag>;
};

const Metric = ({ label, value }: { label: string; value: string }) => (
  <div className="flex justify-between gap-2">
    <span className="text-gray-400">{label}</span>
    <span className="font-mono text-gray-700">{value}</span>
  </div>
);

export const CaseCompareDrawer = ({
  open,
  row,
  runs,
  baselineId,
  index,
  total,
  onClose,
  onNavigate,
}: {
  open: boolean;
  row: CompareRow | null;
  runs: CompareRun[];
  baselineId: string;
  index: number;
  total: number;
  onClose: () => void;
  onNavigate: (delta: number) => void;
}) => {
  const [fullscreen, setFullscreen] = useState(false);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loadingFile, setLoadingFile] = useState(false);

  const ext = (row?.reference?.split(".").pop() || "").toLowerCase();
  const isImage = IMAGE_EXT.includes(ext);

  // First available result for this case — used to fetch the (shared) input file.
  const inputResultId = useMemo(() => {
    for (const r of runs) {
      const c = row?.cells[r.id];
      if (c) return c.id;
    }
    return null;
  }, [runs, row]);

  const expected = useMemo(() => {
    for (const r of runs) {
      const c = row?.cells[r.id];
      if (c?.expectedOutput) return c.expectedOutput;
    }
    return null;
  }, [runs, row]);

  // Lazily load the input image for the current case; revoke on case change / close.
  useEffect(() => {
    let cancelled = false;
    let created: string | null = null;
    setBlobUrl(null);
    if (open && isImage && inputResultId) {
      setLoadingFile(true);
      fetchBenchmarkResultFile(inputResultId)
        .then((b) => {
          if (cancelled) return;
          created = URL.createObjectURL(b);
          setBlobUrl(created);
        })
        .catch(() => undefined)
        .finally(() => {
          if (!cancelled) setLoadingFile(false);
        });
    }
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [open, isImage, inputResultId]);

  // Alt+Up/Down case navigation, suppressed while interacting with text.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (!e.altKey || (e.key !== "ArrowUp" && e.key !== "ArrowDown")) return;
      const el = document.activeElement as HTMLElement | null;
      if (
        el &&
        (el.tagName === "INPUT" ||
          el.tagName === "TEXTAREA" ||
          el.isContentEditable)
      ) {
        return;
      }
      if ((window.getSelection()?.toString().length ?? 0) > 0) return;
      e.preventDefault();
      onNavigate(e.key === "ArrowDown" ? 1 : -1);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onNavigate]);

  const downloadFile = async () => {
    if (!inputResultId) return;
    try {
      const b = await fetchBenchmarkResultFile(inputResultId);
      const url = URL.createObjectURL(b);
      const a = document.createElement("a");
      a.href = url;
      a.download = row?.label || "input";
      a.click();
      // Defer so the browser has begun the download before the blob is freed.
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch {
      /* surfaced via the empty preview state */
    }
  };

  const baselineCell = row?.cells[baselineId] ?? null;

  // Order baseline-first so the output diff tints columns against the user-selected
  // baseline — consistent with the per-run judge deltas and the overview.
  const orderedRuns = useMemo(() => {
    const base = runs.find((r) => r.id === baselineId);
    return base ? [base, ...runs.filter((r) => r.id !== baselineId)] : runs;
  }, [runs, baselineId]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={fullscreen ? "100%" : 1100}
      destroyOnHidden
      title={
        <div className="flex items-center gap-3">
          <span
            className="font-mono text-sm truncate max-w-[24rem]"
            title={row?.reference ?? ""}
          >
            {row?.label ?? "Case"}
          </span>
          {row?.divergent && <Tag color="orange">divergent</Tag>}
        </div>
      }
      extra={
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">
            {total > 0 ? `${index + 1} / ${total}` : ""}
          </span>
          <Tooltip title="Previous case (Alt+↑)">
            <Button
              size="small"
              icon={<LeftOutlined />}
              disabled={index <= 0}
              onClick={() => onNavigate(-1)}
            />
          </Tooltip>
          <Tooltip title="Next case (Alt+↓)">
            <Button
              size="small"
              icon={<RightOutlined />}
              disabled={index >= total - 1}
              onClick={() => onNavigate(1)}
            />
          </Tooltip>
          <Button
            size="small"
            icon={
              fullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />
            }
            onClick={() => setFullscreen((v) => !v)}
          />
        </div>
      }
    >
      {!row ? (
        <Empty description="No case selected" />
      ) : (
        <div className="flex gap-5 flex-col lg:flex-row">
          {/* Shared input + ground truth */}
          <div className="lg:w-80 flex-shrink-0 space-y-4 lg:sticky lg:top-0 self-start">
            <div>
              <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                Input
              </h4>
              {isImage ? (
                <Spin spinning={loadingFile}>
                  {blobUrl ? (
                    <Image
                      src={blobUrl}
                      alt={row.label}
                      className="rounded-lg border border-gray-200"
                    />
                  ) : (
                    <div className="h-40 flex items-center justify-center bg-gray-50 rounded-lg border border-dashed border-gray-200 text-gray-400 text-xs">
                      {loadingFile ? "Loading image…" : "Preview unavailable"}
                    </div>
                  )}
                </Spin>
              ) : (
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2 text-sm text-gray-600 truncate">
                    <FileTextOutlined /> {row.label}
                  </span>
                  {inputResultId && (
                    <Button
                      size="small"
                      icon={<DownloadOutlined />}
                      onClick={downloadFile}
                    >
                      Open
                    </Button>
                  )}
                </div>
              )}
            </div>

            {expected != null && (
              <div>
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                  Expected (ground truth)
                </h4>
                <pre className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs whitespace-pre-wrap break-words max-h-60 overflow-auto">
                  {JSON.stringify(expected, null, 2)}
                </pre>
              </div>
            )}
          </div>

          {/* Per-run comparison */}
          <div className="flex-grow min-w-0 space-y-5">
            <div className="flex gap-3 overflow-x-auto pb-1">
              {orderedRuns.map((run) => {
                const c = row.cells[run.id];
                const judgeDelta =
                  c?.qualityScore != null &&
                  baselineCell?.qualityScore != null &&
                  run.id !== baselineId
                    ? c.qualityScore - baselineCell.qualityScore
                    : null;
                return (
                  <div
                    key={run.id}
                    className="min-w-[13rem] flex-1 rounded-lg border border-gray-200 bg-white p-3 border-l-4"
                    style={{ borderLeftColor: run.color }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span
                        className="text-xs font-semibold text-gray-700 truncate"
                        title={run.label}
                      >
                        {run.label}
                      </span>
                      <Verdict r={c} />
                    </div>
                    {c?.qualityScore != null ? (
                      <div className="flex items-baseline gap-2 mb-2">
                        <span
                          className="text-2xl font-bold"
                          style={{ color: judgeColor(c.qualityScore) }}
                        >
                          {c.qualityScore}
                          <span className="text-sm text-gray-400">/10</span>
                        </span>
                        {judgeDelta != null && judgeDelta !== 0 && (
                          <span
                            className={`text-xs font-semibold ${judgeDelta > 0 ? "text-emerald-600" : "text-rose-600"}`}
                          >
                            {judgeDelta > 0 ? "▲" : "▼"} {Math.abs(judgeDelta)}
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="text-xs text-gray-300 mb-2">
                        no judge score
                      </div>
                    )}
                    <div className="space-y-0.5 text-xs">
                      <Metric
                        label="Latency"
                        value={c?.latencyMs != null ? `${c.latencyMs} ms` : "—"}
                      />
                      <Metric
                        label="Tokens"
                        value={
                          c?.tokensUsed != null ? String(c.tokensUsed) : "—"
                        }
                      />
                      <Metric
                        label="Cost"
                        value={c?.cost != null ? `$${c.cost.toFixed(4)}` : "—"}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            <section>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">
                Output comparison
              </h4>
              <OutputDiff
                runs={orderedRuns}
                payloads={orderedRuns.map(
                  (r) => row.cells[r.id]?.actualOutput ?? null,
                )}
              />
            </section>

            <section>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">
                Judge reasoning
              </h4>
              <Collapse
                size="small"
                items={orderedRuns.map((run) => {
                  const c = row.cells[run.id];
                  return {
                    key: run.id,
                    label: (
                      <span className="flex items-center gap-2 text-sm">
                        <span
                          className="w-2.5 h-2.5 rounded-full inline-block"
                          style={{ backgroundColor: run.color }}
                        />
                        {run.label}
                        {c?.qualityScore != null && (
                          <span className="text-gray-400">
                            · {c.qualityScore}/10
                          </span>
                        )}
                      </span>
                    ),
                    children: (
                      <div className="text-sm text-gray-700 whitespace-pre-wrap">
                        {c?.judgeReasoning || (
                          <span className="text-gray-400">
                            {c
                              ? "No reasoning recorded."
                              : "This run did not include this case."}
                          </span>
                        )}
                        {c?.actualOutput != null && (
                          <p className="text-xs text-gray-400 mt-2">
                            Output summary: {formatOutput(c.actualOutput)}
                          </p>
                        )}
                      </div>
                    ),
                  };
                })}
              />
            </section>
          </div>
        </div>
      )}
    </Drawer>
  );
};

export default CaseCompareDrawer;
